import React, { useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Microphone, X, Waveform, PaperPlaneTilt } from '@phosphor-icons/react';

export function MobileVoiceButton() {
  const { api } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await processAudio(blob);
      };

      mediaRecorder.start();
      setIsListening(true);
      setResult(null);

      // Auto-stop after 10 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop();
          setIsListening(false);
        }
      }, 10000);
    } catch (e) {
      console.error('Mic access denied:', e);
      setResult({ error: 'Microphone access denied. Please allow mic permission.' });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  const processAudio = async (blob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('audio', blob, 'voice_command.webm');
      const res = await api.post('/voice-cmd/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (e) {
      setResult({ error: 'Voice processing failed. Check API key in Settings.' });
    }
    setIsProcessing(false);
  };

  const handleClick = () => {
    if (isListening) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <>
      {/* Voice FAB */}
      <button
        data-testid="mobile-voice-btn"
        onClick={handleClick}
        className={`fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-90 ${isListening ? 'animate-pulse' : ''}`}
        style={{
          background: isListening ? '#EF4444' : isProcessing ? '#6366F1' : 'rgba(255,255,255,0.08)',
          border: `2px solid ${isListening ? '#EF444440' : isProcessing ? '#6366F140' : 'rgba(255,255,255,0.1)'}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        {isProcessing ? (
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : isListening ? (
          <Waveform size={24} weight="bold" className="text-white" />
        ) : (
          <Microphone size={24} weight="fill" className="text-neutral-300" />
        )}
      </button>

      {/* Result Toast */}
      {result && (
        <div className="fixed bottom-36 left-4 right-4 z-50 max-w-sm mx-auto animate-in slide-in-from-bottom-2" data-testid="voice-result-toast">
          <div className="rounded-2xl px-4 py-3" style={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {result.error ? (
                  <p className="text-xs text-red-400">{result.error}</p>
                ) : (
                  <>
                    <p className="text-xs text-neutral-500 mb-1">You said:</p>
                    <p className="text-sm text-white font-medium">{result.transcript || result.text}</p>
                    {result.response && <p className="text-xs text-neutral-400 mt-1">{result.response}</p>}
                  </>
                )}
              </div>
              <button onClick={() => setResult(null)} className="p-1 rounded-lg hover:bg-white/5 flex-shrink-0">
                <X size={14} className="text-neutral-500" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
